import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Copy,
  Crosshair,
  GitCommitVertical,
  Layers,
  UserRound,
  type LucideIcon,
} from 'lucide-react';

// ─── Kontextmenü einer Personenkarte ────────────────────────────────────────
//
// Öffnet sich per Rechtsklick am Mauszeiger. Position wird nach dem Rendern
// korrigiert, damit das Menü nie über den Fensterrand hinausragt.

export interface ContextMenuTarget {
  personId: string;
  personName: string;
  /** Bildschirmkoordinaten des Rechtsklicks. */
  x: number;
  y: number;
}

interface MenuAction {
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
  /** Trennlinie oberhalb dieses Eintrags. */
  separated?: boolean;
}

interface NodeContextMenuProps {
  target: ContextMenuTarget;
  onClose: () => void;
  onOpenDetails: (personId: string) => void;
  onCenter: (personId: string) => void;
  onFocusLineage: (personId: string) => void;
  onSubtreeRoot: (personId: string) => void;
  /** Ist die Linien-Zentrierung für diese Person gerade aktiv? */
  lineageActive: boolean;
}

const MENU_MARGIN = 8;

export function NodeContextMenu({
  target,
  onClose,
  onOpenDetails,
  onCenter,
  onFocusLineage,
  onSubtreeRoot,
  lineageActive,
}: NodeContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: target.x, y: target.y });

  // Klick daneben, Escape oder Scrollen schließt das Menü.
  useEffect(() => {
    function handlePointerDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKey, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKey, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  // Nach dem Rendern in den sichtbaren Bereich schieben.
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const maxX = window.innerWidth - width - MENU_MARGIN;
    const maxY = window.innerHeight - height - MENU_MARGIN;
    setPos({
      x: Math.max(MENU_MARGIN, Math.min(target.x, maxX)),
      y: Math.max(MENU_MARGIN, Math.min(target.y, maxY)),
    });
  }, [target.x, target.y]);

  const run = (fn: () => void) => () => {
    fn();
    onClose();
  };

  const actions: MenuAction[] = [
    {
      label: 'Details öffnen',
      icon: UserRound,
      onSelect: run(() => onOpenDetails(target.personId)),
    },
    {
      label: 'Hierhin zentrieren',
      icon: Crosshair,
      onSelect: run(() => onCenter(target.personId)),
    },
    {
      label: lineageActive ? 'Linien-Zentrierung aufheben' : 'Linie zentrieren',
      icon: GitCommitVertical,
      onSelect: run(() => onFocusLineage(target.personId)),
      separated: true,
    },
    {
      label: 'Teilbaum ab hier',
      icon: Layers,
      onSelect: run(() => onSubtreeRoot(target.personId)),
    },
    {
      label: 'Name kopieren',
      icon: Copy,
      onSelect: run(() => {
        // Ohne sicheren Kontext (http) fehlt die Zwischenablage-API – dann
        // still überspringen statt eine Ausnahme hochblubbern zu lassen.
        void navigator.clipboard?.writeText(target.personName);
      }),
      separated: true,
    },
  ];

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label={`Aktionen für ${target.personName}`}
      className="fixed z-[1400] min-w-[13rem] overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-xl"
      style={{ left: pos.x, top: pos.y }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <p className="truncate border-b border-slate-100 px-3 pb-1.5 pt-1 text-[11px] font-semibold text-slate-500">
        {target.personName}
      </p>
      {actions.map((action) => (
        <button
          key={action.label}
          role="menuitem"
          onClick={action.onSelect}
          className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50 ${
            action.separated ? 'mt-1 border-t border-slate-100 pt-2' : ''
          }`}
        >
          <action.icon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
          {action.label}
        </button>
      ))}
    </div>
  );
}
